/**
 * The `@` half of a composer, as pure functions (RFC 0292 S3).
 *
 * PURE ON PURPOSE, and not because tests are easier. Three surfaces need this
 * behaviour and cannot share a component: the side column's textarea, the
 * document editor's popover, and — through the bridge — the review app's own
 * composer inside a sandboxed iframe. What they can share is the rule for where
 * a mention starts, what choosing one does to the text, and which chips are
 * still true when the person presses Enter.
 *
 * A DRAFT MENTION CARRIES ITS LABEL, which a stored one does not. The label is
 * how `verifyMentions` checks the chip is still sitting on the words it named
 * before the comment is written. Offsets into a draft are honest only while
 * nobody edits above them, and a person editing their own sentence is the
 * normal case, not the exotic one.
 */

import type { Mention, MentionKind } from '@mod/core/model'

/** A mention while it is still being typed: the stored fields plus the text
 *  the chip covers, which is what makes drift detectable. */
export interface DraftMention extends Mention {
  label: string
}

/** A composer's state: the prose, and what it names so far. */
export interface MentionDraft {
  text: string
  mentions: DraftMention[]
}

export const EMPTY_DRAFT: MentionDraft = { text: '', mentions: [] }

/** The `@token` being typed at `caret`, or null when none is. */
export interface ActiveQuery {
  /** What to match candidates against, without the `@`. */
  query: string
  /** Offset of the `@` itself, so choosing a candidate can replace from there. */
  start: number
}

/**
 * Is the caret inside an `@token`?
 *
 * The `@` must open a word — at the start of the body or after whitespace — so
 * an email address in a comment does not open a picker on its domain. Nothing
 * between the `@` and the caret may be whitespace: once you type a space the
 * token is over, whether or not you chose anything.
 */
export function activeMentionQuery(
  text: string,
  caret: number,
): ActiveQuery | null {
  const upto = text.slice(0, caret)
  const at = upto.lastIndexOf('@')
  if (at < 0) return null
  const before = at === 0 ? '' : upto[at - 1]
  if (before && !/\s/.test(before)) return null
  const query = upto.slice(at + 1)
  if (/\s/.test(query)) return null
  return { query, start: at }
}

/** The result of choosing a candidate: the new text, where the caret goes, and
 *  the chip to record. */
export interface AppliedCandidate {
  draft: MentionDraft
  caret: number
}

/**
 * Replace the `@token` at `active` with the candidate's label, and record the
 * chip.
 *
 * A trailing space is added, which is what ends the token: without it the next
 * character typed reopens the picker on a name that has already been chosen. It
 * is NOT added when the text already continues with whitespace, or naming
 * somebody mid-sentence leaves a double space in the middle of their comment.
 * Mentions that sat AFTER the token shift by the length difference, so choosing
 * a second name earlier in a sentence does not leave the first one pointing at
 * the wrong words.
 */
export function applyCandidate(
  draft: MentionDraft,
  active: ActiveQuery,
  candidate: { kind: MentionKind; id: string; label: string },
): AppliedCandidate {
  const consumedEnd = active.start + 1 + active.query.length
  const chip = `@${candidate.label}`
  const continues = draft.text.slice(consumedEnd)
  const inserted = /^\s/.test(continues) ? chip : `${chip} `
  const text = draft.text.slice(0, active.start) + inserted + continues
  const shift = inserted.length - (consumedEnd - active.start)

  const mentions = draft.mentions
    // A chip that overlapped the token being replaced is gone with it.
    .filter((m) => m.at + m.len <= active.start || m.at >= consumedEnd)
    .map((m) => (m.at >= consumedEnd ? { ...m, at: m.at + shift } : m))
  mentions.push({
    kind: candidate.kind,
    id: candidate.id,
    at: active.start,
    len: chip.length,
    label: candidate.label,
  })
  mentions.sort((a, b) => a.at - b.at)
  return { draft: { text, mentions }, caret: active.start + inserted.length }
}

/**
 * The chips that are still true of `text`, as the stored shape.
 *
 * Called on SUBMIT rather than on every keystroke, and it drops rather than
 * repairs. A person who edits above their own chip moves it, and re-anchoring by
 * searching for the label would silently attach the mention to a second
 * occurrence of the same name — a comment that wakes a session nobody pointed
 * at. Dropping loses the chip and keeps the prose, which is the failure the
 * reader can see and fix.
 */
export function verifyMentions(
  text: string,
  mentions: DraftMention[],
): Mention[] {
  const out: Mention[] = []
  for (const m of mentions) {
    if (text.slice(m.at, m.at + m.len) !== `@${m.label}`) continue
    out.push({ kind: m.kind, id: m.id, at: m.at, len: m.len })
  }
  return out
}

/**
 * Re-anchor chips after a plain edit, by the crudest rule that is honest: a
 * chip whose text is unchanged keeps its place, and one whose text moved is
 * searched for ONCE and only when that leaves no ambiguity.
 *
 * Typing at the end of a comment is the common case and must not lose the chips
 * before it, which the unchanged check handles for free. Anything else — the
 * same name twice, a chip partly deleted — drops it, for `verifyMentions`'
 * reason: a mention pointing at words nobody chose is worse than no mention.
 */
export function reanchorMentions(
  text: string,
  mentions: DraftMention[],
): DraftMention[] {
  const out: DraftMention[] = []
  for (const m of mentions) {
    const chip = `@${m.label}`
    if (text.slice(m.at, m.at + m.len) === chip) {
      out.push(m)
      continue
    }
    const first = text.indexOf(chip)
    if (first < 0) continue
    if (text.indexOf(chip, first + 1) >= 0) continue
    out.push({ ...m, at: first, len: chip.length })
  }
  return out
}

/** A body split for rendering: plain runs, and the chips between them. Each
 *  span carries `at`, its offset in the body, which is the only identity a run
 *  of plain text has and so the only honest key for it. */
export type BodySpan =
  | { kind: 'text'; text: string; at: number }
  | { kind: 'mention'; text: string; at: number; mention: Mention }

/**
 * Split a stored body into runs and chips (RFC 0292 §4a step 6).
 *
 * OUT-OF-RANGE AND OVERLAPPING CHIPS ARE IGNORED rather than clamped. A chip is
 * a claim about which characters name something, and a clamped one renders half
 * a word as a link: the prose is the thing that has to survive, so a chip that
 * does not fit is simply not drawn. A reader with no mention support shows the
 * same text, which is the invariant this whole design is for.
 */
export function splitByMentions(
  body: string,
  mentions: Mention[] | undefined,
): BodySpan[] {
  const ordered = [...(mentions ?? [])]
    .filter((m) => m.len > 0 && m.at >= 0 && m.at + m.len <= body.length)
    .sort((a, b) => a.at - b.at)
  const out: BodySpan[] = []
  let cursor = 0
  for (const m of ordered) {
    if (m.at < cursor) continue
    if (m.at > cursor) {
      out.push({ kind: 'text', text: body.slice(cursor, m.at), at: cursor })
    }
    out.push({
      kind: 'mention',
      text: body.slice(m.at, m.at + m.len),
      at: m.at,
      mention: m,
    })
    cursor = m.at + m.len
  }
  if (cursor < body.length) {
    out.push({ kind: 'text', text: body.slice(cursor), at: cursor })
  }
  return out
}
